<script>
import { defineAsyncComponent } from "@common/defineAsyncComponent";
import { sDs } from "@common/sDs/sDs.js";
import { componentStore } from "@common/componentStore/componentStore.js";
import { configStore } from "@common/configStore";
import { library } from "@common/library/library";
import { store } from "@common/store/store";

const controlComponent = defineAsyncComponent(() => import('@common/control/control.vue'));

const editorSettings = {};

const signerManagerCfg = {
    cId: "signerManager",
    cType: "signerManager",
    cTitle: "signerManager",
    cTemplate: `<div v-if="!destroyed && show">
                    <div class="signers-wrapper">
                        <div v-if="!editingSigners">
                            <control name="SIGNER" :items="signerItems" :disabled="isDisabled"></control>
                            <control name="SIGNERS_EDIT" :disabled="isDisabled"></control>
                        </div>
                        <div v-show="editingSigners">
                            <control name="SET_SIGNING_ORDER" :value="signingOrder" class="set-signing-order-checkbox"></control>
                            <div class="clearfix"></div>
                            <control v-if="!reloadSigners" name="SIGNERS" :items="signers" :config="{isDraggable: signingOrder}"></control>
                            <div class="clearfix">
                                <div v-show="tmpTemplateSigners.length < 10" class="sub-list-signer-item-container">
                                    <div class="signers-list-main-details">
                                        <div>
                                            <control name="ADD_SIGNER"></control>
                                            <div class="clearfix"></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="signers-actions" v-if="!inPopup">
                                <control name="SIGNERS_SAVE"></control>
                                <control name="SIGNERS_CANCEL"></control>
                            </div>
                        </div>
                    </div>
                    <input v-if="getRecordIdFromForm" type="hidden" name="JSON_SIGNERS" :value="jsonSigners"/>
                </div>`,
    controls: [
        {
            name: "SET_SIGNING_ORDER",
            type: "checkbox",
            label: "Set signing order",
            onchange: "setSigningOrder",
            helpText: 'When "Set signing order" checkbox is checked, email will be sent sequentially, otherwise simultaneously to all signers.',
            showWithEmptyLabel: true
        },
        {
            name: "SIGNERS_EDIT",
            type: "button",
            label: "Edit",
            onclick: "editSigners",
            width: '60px'
        },
        {
            name: "SIGNERS_SAVE",
            type: "button",
            label: "Save Signers",
            onclick: "saveSigners",
            cssClass: 'e-success',
        },
        {
            name: "SIGNERS_CANCEL",
            type: "button",
            label: "Cancel",
            onclick: "cancelSigners",
            cssClass: 'e-danger',
        },
        {
            name: 'SIGNER',
            type: 'dropdown',
            label: 'Signer',
            onchange: 'changeSigner',
            hideLabel: true,
            fields: { text: 'text', value: 'value' },
            default: 0,
            width: '240px',
            showClearButton: false,
            doNotRemoveOnDestroy: true,
            cssClass: 'pdf-signer-dd',
            itemTemplate: '<span class="e-list-item-header signer-list-item" ><!--<div class="signer-dropdown-color" :style="{\'background-color\': data.color}"></div>--><div>{{data.text}}</div></span>',
            valueTemplate: '<span class="e-list-item-header signer-list-item" ><!--<div class="signer-value-color" :style="{\'background-color\': data.color}"></div>--><div class="signer-value">{{data.text}}</div></span>',
        },
        {
            name: 'SIGNER_TYPE',
            type: "dropdown",
            fields: { text: 'text', value: 'value' },
            items: [
                {
                    text: 'Dynamic signer',
                    value: 0
                },
                {
                    text: 'Static signer',
                    value: 1
                },
            ],
            width: '110px',
            showClearButton: false,
            doNotRemoveOnDestroy: true,
            onchange: 'changeSignerType'
        },
        {
            name: 'SIGNER_ID',
            type: "dropdowntree",
            filterType: 'Contains',
            allowFiltering: true,
            fields: {
                text: 'text',
                value: 'value',
                hasChildren: 'hasChild',
                parentValue: 'pId'
            },
            width: '270px',
            notShowMissingOptions: true,
            doNotRemoveOnDestroy: true,
            onchange: 'changeSignerId'
        },
        {
            name: 'SIGNER_EMAIL',
            type: 'string',
            onchange: 'changeSignerEmail',
            doNotRemoveOnDestroy: true,
            width: '270px'
        },
        {
            name: 'SIGNER_EST',
            type: "dropdowntree",
            filterType: 'Contains',
            allowFiltering: true,
            fields: {
                text: 'text',
                value: 'value',
                hasChildren: 'hasChild',
                parentValue: 'pId'
            },
            onchange: 'changeSignerEST',
            doNotRemoveOnDestroy: true,
            width: '386px',
            placeholder: 'Assign email template',
            itemsSource: 'configStore.allESTOptions("vol")',
        },
        {
            name: 'SIGNER_ORDER',
            type: "button",
            label: '',
            width: '30px',
            cssClass: 'e-link',
            doNotRemoveOnDestroy: true,
            iconCss: 'e-icons e-delete-icon'
        },
        {
            name: 'DELETE_SIGNER',
            type: "button",
            label: '',
            width: '30px',
            cssClass: 'e-link',
            onclick: 'deleteSigner',
            iconCss: 'e-icons e-delete-icon'
        },
        {
            name: 'ADD_SIGNER',
            type: "button",
            label: 'Add Signer',
            width: '100%',
            onclick: 'addSigner'
        },
        {
            name: 'SIGNERS',
            type: 'list',
            onchangeorder: 'updateSignersAfterDnD',
            itemContentTemplate: `<div v-if="data" class="sub-list-signer-item-container" :class="{'signer-current-email': (data.CONTACT_TYPE_ID == 7 && data.SIGNER_PSP === 101) || data.key === 0}">
                            <div class="signer-color" :style="{'background-color': data.color}"></div>
                            <div class="signers-list-main-details">
                                <div v-if="data.emailValue === '[CURRENT_EMAIL]'">
                                    <div v-show="controlValue('SET_SIGNING_ORDER') && !data.isNotDraggable" class="control drag-and-drop-signers"><span class="e-btn-icon e-icons e-drag-and-drop-indicator"></span></div>
                                    <div class="hidden">
                                        <control v-show="false" name="SIGNER_TYPE" :value="data.isStatic" :actionParams="data.key" :disabled="data.disabled"></control>
                                        <control v-show="false" v-if="!data.isStatic" name="SIGNER_ID" :items="data.signers" :value="data.idValue" :actionParams="data.key" :disabled="data.disabled" :width="controlValue('SET_SIGNING_ORDER') ? '270px' : '305px'"></control>
                                        <control v-show="false" v-if="data.isStatic" name="SIGNER_EMAIL" :value="data.emailValue" :disabled="data.disabled" :actionParams="data.key" :width="controlValue('SET_SIGNING_ORDER') ? '270px' : '305px'"></control>
                                    </div>
                                    <div class="current-volunteer-email">Volunteer Email</div>
                                    <control name="DELETE_SIGNER" :actionParams="data.key" :display="!data.disabled"></control>
                                    <control v-if="data.isStatic && data.key !== 0" name="SIGNER_EST" :value="data.estValue" :disabled="data.disabled" :actionParams="data.key" :width="controlValue('SET_SIGNING_ORDER') ? '270px' : '305px'"></control>
                                    <div class="clearfix"></div>
                                </div>
                                <div v-else>
                                    <div v-show="controlValue('SET_SIGNING_ORDER')" class="control drag-and-drop-signers"><span class="e-btn-icon e-icons e-drag-and-drop-indicator"></span></div>
                                    <control name="SIGNER_TYPE" :value="data.isStatic" :actionParams="data.key" :disabled="data.disabled"></control>
                                    <control v-if="!data.isStatic" name="SIGNER_ID" :items="data.signers" :value="data.idValue" :actionParams="data.key" :disabled="data.disabled" :width="controlValue('SET_SIGNING_ORDER') ? '270px' : '305px'"></control>
                                    <control v-if="data.isStatic" name="SIGNER_EMAIL" :value="data.emailValue" :disabled="data.disabled" :actionParams="data.key" :width="controlValue('SET_SIGNING_ORDER') ? '270px' : '305px'"></control>
                                    <control name="DELETE_SIGNER" :actionParams="data.key" :display="!data.disabled"></control>
                                    <control v-show="!(data.CONTACT_TYPE_ID == 7 && data.SIGNER_PSP === 101) && data.key !== 0" name="SIGNER_EST" :value="data.estValue" :disabled="data.disabled" :actionParams="data.key"></control>
                                    <div class="clearfix"></div>
                                </div>
                            </div>
                            <div class="clearfix"></div>
                        </div>`
        }
    ]
};

const signerManagerCmp = {
    name: 'signerManager',
    editorSettings: editorSettings,
    preInitControls: true,
    props: {
        dpId: {
            default: false
        },
        templateId: {
            default: false
        },
        editingSigners: {
            default: false
        },
        inPopup: {
            default: false
        },
        showSignersAsPopup: {
            default: false
        },
        changeSignerFnc: {
            default: false
        },
        getRecordIdFromForm: {
            default: false
        }
    },
    data() {
        return {
            // editingSigners: false,
            isDisabled: false,
            cursorPosition: {},
            destroyed: false,
            fileNotLoaded: false,
            templates: [],
            selected: false,
            actions: [
                { text: 'Edit Template', value: 'edit', disabled: true },
                { text: 'Delete Template', value: 'delete', disabled: true },
                { text: 'New Template', value: 'new' }
            ],
            show: false,
            controlsItems: [],
            fieldsItems: [],
            emailItems: [],
            base64: false,
            selectedGroup: false,
            selectedGroupName: false,
            selectedFieldGroup: false,
            selectedFieldGroupName: false,
            selectedControl: false,
            templatesLoaded: true,
            tryToAddInitialCount: 0,
            tryToAddInitialToSignatureCount: 0,
            tryToHideDisableCount: 0,
            contextMenuSettings: {
                contextMenuItems: ['Delete']
            },
            wrapperID: '',
            pdfLoaded: false,
            foundedFields: [],
            prevFieldsValues: {},
            allowedTypes: ['FLOAT', 'STRING', 'TEXT', 'LIST', 'DATE', 'BOOLEAN', 'INTEGER'],
            notAllowedUDFTypes: [
                217, // dropdown with values
                214, // attachments
                220, // eSignature
                207, // divider
                218 // waiver choice
            ],
            canBeInsertedMultipleTimes: ['SIGNATURE', 'INITIAL', 'PDFLEADERINITIAL', 'PDFLEADERSIGNATURE', 'CURRENT_DATE', 'CURRENT_DATE_TIME'],
            forceControls: [],
            usedControls: [],
            zoomValue: 101,
            formFielClicked: {},
            templateName: false,
            fileUniqueID: false,
            pdfOtherError: false,
            initialValue: '',
            pdfRequiredFields: [],
            defaultTemplateSigners: [{
                CONTACT_TYPE_ID: null,
                CONTACT_TYPE_REF_TABLE: null,
                CUSTOM_ATTRIBUTE_ID: null,
                SIGNER_PSP: 1,
                SIGNER_ORDER: 1,
                SIGNER_EMAIL: "[CURRENT_EMAIL]",
                SIGNER_EST: null
            }],
            templateSigners: [{
                CONTACT_TYPE_ID: null,
                CONTACT_TYPE_REF_TABLE: null,
                CUSTOM_ATTRIBUTE_ID: null,
                SIGNER_PSP: 1,
                SIGNER_ORDER: 1,
                SIGNER_EMAIL: "[CURRENT_EMAIL]",
                SIGNER_EST: null
            }],
            tmpTemplateSigners: [],
            selectedSignerIndex: 0,
            reloadSigners: false,
            signingOrder: false,
        };
    },
    computed: {
        jsonSigners() {
            let tmpSigners = library.cloneObj(this.templateSigners);
            let jsonSigners = "";
            if (!library.objIsEmpty(tmpSigners) && !library.isEqualObj(this.defaultTemplateSigners, tmpSigners)) {
                jsonSigners = JSON.stringify(tmpSigners);
            }
            return jsonSigners;
        },
        signerItems() {
            let signerItems = [];
            if (configStore.state && configStore.state.cfg && configStore.state.cfg.availableFields && this.templateSigners.length) {
                for (let i in this.templateSigners) {
                    let tmpSigner = {};
                    let pspName = 'vol';
                    switch (this.templateSigners[i].SIGNER_PSP) {
                        case 1:
                            pspName = 'vol';
                            break;
                        case 2:
                            pspName = 'opp';
                            break;
                        case 3:
                            pspName = 'org';
                            break;
                        case 101:
                            pspName = 'client';
                            break;
                    }
                    if (typeof this.templateSigners[i].SIGNER_EMAIL === 'string' || this.templateSigners[i].SIGNER_PSP === 101) {
                        let tmpText = this.templateSigners[i].SIGNER_EMAIL;
                        if (this.templateSigners[i].SIGNER_EMAIL === '[CURRENT_EMAIL]') tmpText = 'Volunteer';
                        if (this.templateSigners[i].SIGNER_PSP === 101) tmpText = 'eC User';
                        tmpSigner = {
                            text: tmpText
                        };
                    } else {
                        if (parseInt(this.templateSigners[i].CUSTOM_ATTRIBUTE_ID, 10) > 0) {
                            if (configStore.state.cfg.availableFields[pspName] && configStore.state.cfg.availableFields[pspName]['CUSTOM_ATTRIBUTE#' + this.templateSigners[i].CUSTOM_ATTRIBUTE_ID]) {
                                tmpSigner = {
                                    text: configStore.state.cfg.availableFields[pspName]['CUSTOM_ATTRIBUTE#' + this.templateSigners[i].CUSTOM_ATTRIBUTE_ID].displayTitle
                                };
                            }
                        } else if (this.templateSigners[i].CONTACT_TYPE_ID && this.templateSigners[i].CONTACT_TYPE_REF_TABLE) {
                            let tmpRealName = pspName.toUpperCase() + '_CONTACT#' + this.templateSigners[i].CONTACT_TYPE_ID;
                            switch (this.templateSigners[i].CONTACT_TYPE_REF_TABLE) {
                                case 'M_VOL_EMERGENCY_INFO':
                                    tmpRealName = pspName.toUpperCase() + '_EM_CONTACT#' + this.templateSigners[i].CONTACT_TYPE_ID;
                                    break;
                            }
                            if (configStore.state.cfg.availableFields[pspName] && configStore.state.cfg.availableFields[pspName][tmpRealName]) {
                                tmpSigner = {
                                    text: configStore.state.cfg.availableFields[pspName][tmpRealName].displayTitle
                                };
                            }
                        }
                    }
                    if (!library.objIsEmpty(tmpSigner)) {
                        tmpSigner.value = signerItems.length;
                        signerItems.push(tmpSigner);
                    }
                }
            }
            for (let i in signerItems) {
                signerItems[i].color = this.getSignerColor(i);
            }
            if (!this.inPopup) {
                let tmpUseAsPdf = document.getElementById('use_as_pdf');
                if (tmpUseAsPdf) {
                    if (signerItems.length > 1) {
                        tmpUseAsPdf.setAttribute('disabled', true);
                    } else {
                        tmpUseAsPdf.removeAttribute('disabled');
                    }
                }
            }
            return signerItems;
        },
        signers() {
            let signers = [];
            for (let i in this.tmpTemplateSigners) {
                if (parseInt(i, 10) === 0) this.tmpTemplateSigners[i].SIGNER_EST = null;
                let tmpSigner = {};
                let pspName = 'vol';
                switch (this.tmpTemplateSigners[i].SIGNER_PSP) {
                    case 1:
                        pspName = 'vol';
                        break;
                    case 2:
                        pspName = 'opp';
                        break;
                    case 3:
                        pspName = 'org';
                        break;
                    case 101:
                        pspName = 'client';
                        break;
                }
                if (typeof this.tmpTemplateSigners[i].SIGNER_EMAIL === 'string') {
                    tmpSigner = library.cloneObj(this.tmpTemplateSigners[i]);
                    tmpSigner.text = this.tmpTemplateSigners[i].SIGNER_EMAIL;
                    tmpSigner.emailValue = this.tmpTemplateSigners[i].SIGNER_EMAIL;
                    tmpSigner.isStatic = 1;
                } else {
                    if (parseInt(this.tmpTemplateSigners[i].CUSTOM_ATTRIBUTE_ID, 10) > 0) {
                        if (configStore.state.cfg.availableFields[pspName] && configStore.state.cfg.availableFields[pspName]['CUSTOM_ATTRIBUTE#' + this.tmpTemplateSigners[i].CUSTOM_ATTRIBUTE_ID]) {
                            tmpSigner = library.cloneObj(this.tmpTemplateSigners[i]);
                            tmpSigner.text = configStore.state.cfg.availableFields[pspName]['CUSTOM_ATTRIBUTE#' + this.tmpTemplateSigners[i].CUSTOM_ATTRIBUTE_ID].displayTitle;
                            tmpSigner.idValue = ['CUSTOM_ATTRIBUTE#' + this.tmpTemplateSigners[i].CUSTOM_ATTRIBUTE_ID];
                            tmpSigner.isStatic = 0;
                        } else {
                            tmpSigner = library.cloneObj(this.tmpTemplateSigners[i]);
                            tmpSigner.SIGNER_PSP = 1;
                            tmpSigner.text = 'CUSTOM_ATTRIBUTE#' + this.tmpTemplateSigners[i].CUSTOM_ATTRIBUTE_ID;
                            tmpSigner.idValue = ['CUSTOM_ATTRIBUTE#' + this.tmpTemplateSigners[i].CUSTOM_ATTRIBUTE_ID];
                            tmpSigner.isStatic = 0;
                        }
                    } else if (this.tmpTemplateSigners[i].CONTACT_TYPE_ID && this.tmpTemplateSigners[i].CONTACT_TYPE_REF_TABLE) {
                        let tmpRealName = pspName.toUpperCase() + '_CONTACT#' + this.tmpTemplateSigners[i].CONTACT_TYPE_ID;
                        switch (this.tmpTemplateSigners[i].CONTACT_TYPE_REF_TABLE) {
                            case 'M_VOL_EMERGENCY_INFO':
                                tmpRealName = pspName.toUpperCase() + '_EM_CONTACT#' + this.tmpTemplateSigners[i].CONTACT_TYPE_ID;
                                break;
                        }
                        if (configStore.state.cfg.availableFields[pspName] && configStore.state.cfg.availableFields[pspName][tmpRealName]) {
                            tmpSigner = library.cloneObj(this.tmpTemplateSigners[i]);
                            tmpSigner.text = configStore.state.cfg.availableFields[pspName][tmpRealName].displayTitle;
                            tmpSigner.idValue = [tmpRealName];
                            tmpSigner.isStatic = 0;
                        } else if (tmpRealName === 'CLIENT_CONTACT#7') {
                            tmpSigner = library.cloneObj(this.tmpTemplateSigners[i]);
                            tmpSigner.text = 'eC User';
                            tmpSigner.idValue = [tmpRealName];
                            tmpSigner.isStatic = 0;
                        }
                    }
                }
                if (!library.objIsEmpty(tmpSigner)) {
                    let signerEst = this.tmpTemplateSigners[i].SIGNER_EST ? [this.tmpTemplateSigners[i].SIGNER_EST + ''] : null;
                    tmpSigner.estValue = signerEst;
                    tmpSigner.color = this.getSignerColor(signers.length);
                    tmpSigner.signers = this.emailItems;
                    signers.push(tmpSigner);
                }
            }
            return signers;
        },
        realWrapperID() {
            if (this.wrapperID === '') {
                this.wrapperID = this.cId + '_wrapper' + library.getUniqueID();
            }
            return this.wrapperID;
        },
        id() {
            let ref = this.dataTestId ? this.dataTestId + '_control' : '';
            return ref.replaceAll('__', '_').replaceAll('null', ''); // pdf-viewer is not working when id contains __ or null
        },
    },
    methods: {
        disableControls(flag) {
            this.isDisabled = flag;
        },
        changeSignerId(event, field, index) {
            if (!event.e) return;
            let contactTypeID = null;
            let contactTypeRefTable = null;
            let customAttributeID = null;
            let idValue = field.value[0];
            let signerPSP = 1;
            if (idValue) {
                let item = this.emailItems.find((tmp) => tmp.value === idValue);
                if (item && item.additionalData && item.additionalData.PERSPECTIVE_ID) {
                    signerPSP = item.additionalData.PERSPECTIVE_ID;
                }
                if (idValue.indexOf('_CONTACT#') !== -1) {
                    let tmp = idValue.split('#');
                    if (tmp && tmp.length === 2) {
                        contactTypeID = parseInt(tmp[1], 10);
                        contactTypeRefTable = item.additionalData.CONTACT_TYPE_REF_TABLE;
                    }
                } else if (idValue.indexOf('CUSTOM_ATTRIBUTE#') !== -1) {
                    let tmp = idValue.split('#');
                    if (tmp && tmp.length === 2) {
                        customAttributeID = parseInt(tmp[1], 10);
                    }
                }
            }
            if (!contactTypeID && !customAttributeID) {
                customAttributeID = 1;
            }
            this.reloadSigners = true;
            this.tmpTemplateSigners[index] = {
                CONTACT_TYPE_ID: contactTypeID,
                CONTACT_TYPE_REF_TABLE: contactTypeRefTable,
                CUSTOM_ATTRIBUTE_ID: customAttributeID,
                ESIGNATURE_TEMPLATE_ID: this.tmpTemplateSigners[index].ESIGNATURE_TEMPLATE_ID || this.attachId,
                SIGNER_EMAIL: null,
                SIGNER_EST: this.tmpTemplateSigners[index].SIGNER_EST,
                SIGNER_ORDER: this.tmpTemplateSigners[index].SIGNER_ORDER,
                SIGNER_PSP: signerPSP
            };
            this.$nextTick(() => {
                this.reloadSigners = false;
            });
        },
        changeSignerEST(event, field, index) {
            if (!event.e) return;
            let value = !library.objIsEmpty(field.value) ? field.value[0] : null;
            let tmpSigner = library.cloneObj(this.tmpTemplateSigners[index]);
            tmpSigner.SIGNER_EST = value;
            if (!library.isEqualObj(tmpSigner, this.tmpTemplateSigners[index])) {
                this.reloadSigners = true;
                this.tmpTemplateSigners[index] = tmpSigner;
                this.$nextTick(() => {
                    this.reloadSigners = false;
                });
            }
        },
        changeSignerEmail(event, field, index) {
            if (!event.event) return;
            let value = field.value || '';
            let tmpSigner = {
                CONTACT_TYPE_ID: null,
                CONTACT_TYPE_REF_TABLE: null,
                CUSTOM_ATTRIBUTE_ID: null,
                ESIGNATURE_TEMPLATE_ID: this.tmpTemplateSigners[index].ESIGNATURE_TEMPLATE_ID || this.attachId,
                SIGNER_EMAIL: value,
                SIGNER_EST: this.tmpTemplateSigners[index].SIGNER_EST,
                SIGNER_ORDER: this.tmpTemplateSigners[index].SIGNER_ORDER,
                SIGNER_PSP: 1
            };
            if (!library.isEqualObj(tmpSigner, this.tmpTemplateSigners[index])) {
                this.reloadSigners = true;
                this.tmpTemplateSigners[index] = tmpSigner;
                this.$nextTick(() => {
                    this.reloadSigners = false;
                });
            }
        },
        changeSignerType(event, field, index) {
            if (!event.e) return;
            let isStatic = field.value;
            let tmpTemplateSigners = library.cloneObj(this.tmpTemplateSigners);
            let tmpSigner = {
                CONTACT_TYPE_ID: null,
                CONTACT_TYPE_REF_TABLE: null,
                CUSTOM_ATTRIBUTE_ID: !isStatic ? 1 : null,
                ESIGNATURE_TEMPLATE_ID: tmpTemplateSigners[index].ESIGNATURE_TEMPLATE_ID || this.attachId,
                SIGNER_EMAIL: isStatic ? '' : null,
                SIGNER_EST: tmpTemplateSigners[index].SIGNER_EST,
                SIGNER_ORDER: tmpTemplateSigners[index].SIGNER_ORDER,
                SIGNER_PSP: tmpTemplateSigners[index].SIGNER_PSP
            };
            if (!library.isEqualObj(tmpSigner, this.tmpTemplateSigners[index])) {
                this.reloadSigners = true;
                tmpTemplateSigners[index] = tmpSigner;
                this.tmpTemplateSigners = tmpTemplateSigners;
                this.$nextTick(() => {
                    this.reloadSigners = false;
                });
            }
        },
        validateSigners() {
            let valid = true;
            let templateSigners = library.cloneObj(this.tmpTemplateSigners);
            let usedEmails = [];
            let isEcExists = false;
            for (let i in templateSigners) {
                let isStatic = templateSigners[i].SIGNER_EMAIL;
                let idValue;
                if (templateSigners[i].CUSTOM_ATTRIBUTE_ID) {
                    idValue = 'CUSTOM_ATTRIBUTE#' + templateSigners[i].CUSTOM_ATTRIBUTE_ID;
                } else if (templateSigners[i].CONTACT_TYPE_ID) {
                    let tmpName;
                    switch (templateSigners[i].CONTACT_TYPE_REF_TABLE) {
                        case 'CLIENT_PROPERTY':
                            tmpName = 'CLIENT_CONTACT';
                            break;
                        case 'M_OPP_CONTACT_INFO':
                            tmpName = 'OPP_CONTACT';
                            break;
                        default:
                            tmpName = 'VOL_CONTACT';
                            break;
                    }
                    idValue = tmpName + '#' + templateSigners[i].CONTACT_TYPE_ID;
                }
                let emailValue = templateSigners[i].SIGNER_EMAIL;
                let signerEst = templateSigners[i].SIGNER_EST;
                let tmpEmail;
                if (isStatic) {
                    tmpEmail = emailValue;
                    if (!tmpEmail) {
                        valid = false;
                        library.showDialog('Please fill email address', false, false, { title: 'Warning', showAsAlert: true });
                        break;
                    } else if (tmpEmail !== '[CURRENT_EMAIL]' && !library.checkEmail(tmpEmail)) {
                        valid = false;
                        library.showDialog(tmpEmail + ' is not valid Email address', false, false, { title: 'Warning', showAsAlert: true });
                        break;
                    }
                } else {
                    if (!idValue || idValue === 'CUSTOM_ATTRIBUTE#1') {
                        valid = false;
                        library.showDialog('Please select dynamic email address', false, false, { title: 'Warning', showAsAlert: true });
                        break;
                    } else {
                        tmpEmail = idValue;
                    }
                }
                if (tmpEmail && usedEmails.indexOf(tmpEmail) !== -1) {
                    valid = false;
                    library.showDialog('Please remove duplicate signers', false, false, { title: 'Warning', showAsAlert: true });
                    break;
                } else {
                    usedEmails.push(tmpEmail);
                }
                if (parseInt(this.dpId, 10) > 0 && idValue !== 'CLIENT_CONTACT#7' && !signerEst && parseInt(i, 10) !== 0) {
                    valid = false;
                    library.showDialog('Please select email template', false, false, { title: 'Warning', showAsAlert: true });
                    break;
                }
                if (idValue === 'CLIENT_CONTACT#7') {
                    isEcExists = true;
                    if (parseInt(i, 10) !== 0) {
                        valid = false;
                        library.showDialog('eC User can be first signer only', false, false, { title: 'Warning', showAsAlert: true });
                        break;
                    }
                }
            }
            /* if (valid && !isEcExists && templateSigners[0] && templateSigners[0].SIGNER_EMAIL !== '[CURRENT_EMAIL]') {
                valid = false;
                library.showDialog('Volunteer can be first signer only', false, false, { title: 'Warning', showAsAlert: true });
            } */
            return valid;
        },
        addSigner() {
            let templateSigners = library.cloneObj(this.tmpTemplateSigners);
            if (!this.validateSigners()) return;
            this.reloadSigners = true;
            templateSigners.push({
                CONTACT_TYPE_ID: null,
                CONTACT_TYPE_REF_TABLE: null,
                CUSTOM_ATTRIBUTE_ID: 1,
                ESIGNATURE_TEMPLATE_ID: this.attachId,
                SIGNER_EMAIL: null,
                SIGNER_EST: null,
                SIGNER_ORDER: this.signingOrder ? (templateSigners.length) : 1,
                SIGNER_PSP: 1
            });
            this.tmpTemplateSigners = templateSigners;
            this.changeSigningOrder();
            this.$nextTick(() => {
                this.reloadSigners = false;
            });
        },
        deleteSigner(event, field, index) {
            library.showDialog('Are you sure you want to delete this signer?', () => {
                this.reloadSigners = true;
                let items = library.cloneObj(this.tmpTemplateSigners);
                items.splice(index, 1);
                this.tmpTemplateSigners = items;
                this.changeSigningOrder();
                this.$nextTick(() => {
                    this.reloadSigners = false;
                });
            }, false, { name: 'deleteSigner' });
        },
        getSignerColor(index) {
            let selectedIndex = this.selectedSignerIndex;
            if (typeof index !== 'undefined') {
                selectedIndex = index;
            }
            selectedIndex = parseInt(selectedIndex, 10);
            let colors = [
                '#daeaf7',
                '#ffd65b',
                '#acdce6',
                '#c0a5cf',
                '#97c9bf',
                '#f7b994',
                '#c3d5e6',
                '#cfdb7f',
                '#ff9980',
                '#e6c6e6'
            ];
            return colors[selectedIndex % 10];
        },
        changeSigner(event) {
            this.selectedSignerIndex = event.value || 0;
            if (typeof this.changeSignerFnc === 'function') this.changeSignerFnc(this.selectedSignerIndex);
            if (this.getRecordIdFromForm && typeof changeSigner === 'function') {
                changeSigner(this.selectedSignerIndex, event.itemData.text);
            }
        },
        getSelectedSigner() {
            return { index: this.selectedSignerIndex, text: this.controls?.['SIGNER']?.items?.[this.controls?.['SIGNER']?.value]?.text };
        },
        setSigningOrder(event) {
            this.signingOrder = event.checked;
            this.reloadSigners = true;
            let tmpTemplateSigners = library.cloneObj(this.tmpTemplateSigners);
            for (let i in tmpTemplateSigners) {
                tmpTemplateSigners[i].SIGNER_ORDER = this.signingOrder ? parseInt(i, 10) + 1 : 1;
            }
            this.tmpTemplateSigners = tmpTemplateSigners;
            if (this.controls && this.controls['SIGNERS']) {
                this.controls['SIGNERS'].isDraggable = this.signingOrder;
            }
            this.$nextTick(() => {
                this.reloadSigners = false;
            });
        },
        editSigners() {
            this.tmpTemplateSigners = library.cloneObj(this.templateSigners);
            if (this.controls && this.controls['SIGNERS']) {
                this.controls['SIGNERS'].isDraggable = this.signingOrder;
            }
            if (this.showSignersAsPopup) {
                let uniqueID = library.getUniqueID();
                let self = this;
                let signerManagerPopup;
                let signersDialogTemplate = {
                    name: "upload-attachments-popup_" + uniqueID,
                    template: `
                        <signer-manager ref="signerManagerPopup" c-id="signerManager__popup" :prop-editing-signers="true" :prop-in-popup="true" :prop-template-id="templateId" :prop-dp-id="dpId"></signer-manager>
                    `,
                    data() {
                        return {
                            templateId: self.templateId,
                            dpId: self.dpId,
                        };
                    },
                    mounted() {
                        signerManagerPopup = this?.$refs['signerManagerPopup'];
                        if (signerManagerPopup) {
                            signerManagerPopup.tmpTemplateSigners = library.cloneObj(self.templateSigners);
                            let orders = [];
                            for (let j in signerManagerPopup.tmpTemplateSigners) {
                                if (orders.indexOf(signerManagerPopup.tmpTemplateSigners[j].SIGNER_ORDER) === -1) {
                                    orders.push(signerManagerPopup.tmpTemplateSigners[j].SIGNER_ORDER);
                                }
                            }
                            signerManagerPopup.signingOrder = orders.length > 1;
                        }
                    },
                    methods: {
                    },
                    components: {
                        store,
                        control: controlComponent
                    },
                };
                library.showDialog(signersDialogTemplate, async () => {
                    let response = {
                        data: []
                    };
                    let status = 'success';
                    if (!signerManagerPopup.validateSigners()) {
                        status = 'failure';
                    } else {
                        self.show = false;
                        signerManagerPopup.saveSigners();
                        self.templateSigners = library.cloneObj(signerManagerPopup.templateSigners);
                        self.$nextTick(() => {
                            self.show = true;
                        });
                    }
                    response.status = status;
                    return response;
                }, false, {
                    title: 'Signers',
                    name: 'signers',
                    preventCloseOnBackdropClick: true,
                    okButtonLabel: 'Save',
                    width: '540px',
                    showCloseIcon: true,
                    closeOnEscape: false,
                    isComponentDialog: true,
                    doNotCloseOnFuncFailure: true
                });
            } else {
                this.editingSigners = true;
            }
        },
        async manualSave() {
            if (!this.getRecordIdFromForm) {
                await sDs.post('sds_setSigners', this.dpId, this.templateId, this.templateSigners);
            }
        },
        saveSigners() {
            let tmpSigners = [];
            if (!this.validateSigners()) return;
            this.templateSigners = library.cloneObj(this.tmpTemplateSigners);
            this.editingSigners = false;
        },
        cancelSigners() {
            this.editingSigners = false;
        },
        async onLoad() {
            if (this.getRecordIdFromForm) {
                this.dpId = document?.forms?.[0]?.packageID?.value || 1;
                this.templateId = document?.forms?.[0]?.templateID?.value || 0;
            }
            let promises = [
                configStore.getAvailableFieldList('opp'),
                configStore.getAvailableFieldList('vol')
            ];
            let tmpEmailControls = [];
            let response = await library.promiseAllSettled(promises);
            for (let i in response) {
                if (i === 'length') continue;
                let controls = response[i].value;
                let groupTitle = '';
                let pspID = 1;
                if (controls) {
                    switch (i) {
                        case "1":
                            groupTitle = 'Volunteer';
                            pspID = 1;
                            break;
                        case "0":
                            groupTitle = 'Opportunity';
                            pspID = 2;
                            break;
                    }
                    if (groupTitle) {
                        let childEmailElements = [];
                        for (let j in controls) {
                            if (j === 'length') continue;
                            if ((i === "0" || i === "1") && controls[j].IS_EMAIL) {
                                if (typeof controls[j].displayTitle !== 'undefined') {
                                    let additionalData = library.cloneObj(controls[j]);
                                    additionalData.PERSPECTIVE_ID = pspID;
                                    childEmailElements.push({
                                        text: controls[j].displayTitle.replace('MEM::', 'CLI::'),
                                        value: j,
                                        additionalData: additionalData,
                                        pId: groupTitle
                                    });
                                }
                            }
                        }
                        if (childEmailElements.length) {
                            tmpEmailControls.push({
                                text: groupTitle,
                                value: groupTitle,
                                hasChild: true,
                                selectable: false,
                                htmlAttributes: { 'prevent-select': 'true' },
                            });
                            tmpEmailControls = [...tmpEmailControls, ...childEmailElements];
                        }
                    }
                }
            }
            if (parseInt(this.dpId, 10) > 0) {
                tmpEmailControls.push({
                    text: 'eC User',
                    value: 'CLIENT_CONTACT#7',
                    additionalData: {
                        "displayTitle": "eC User",
                        "MULTIPLE": 0,
                        "DEFAULT_WIDTH": 200,
                        "TYPE": 301,
                        "CONTACT_TYPE_REF_TABLE": "CLIENT_PROPERTY",
                        "IS_EMAIL": true,
                        "SIMPLE_TYPE": "STRING",
                        "PERSPECTIVE_ID": 101
                    }
                });
            }
            if (!this.inPopup) {
                let tmpSignersResponse = await sDs.post('sds_getSigners', this.dpId, this.templateId);
                if (tmpSignersResponse && tmpSignersResponse.status === 'success') {
                    let tmpSigners = library.cloneObj(tmpSignersResponse.data);
                    for (let i in tmpSigners) {
                        delete tmpSigners[i].SIGNER_ID;
                    }
                    tmpSigners = library.uniqueArrayWithObjects(tmpSigners);
                    let orders = [];
                    for (let j in tmpSigners) {
                        if (orders.indexOf(tmpSigners[j].SIGNER_ORDER) === -1) {
                            orders.push(tmpSigners[j].SIGNER_ORDER);
                        }
                    }
                    this.signingOrder = orders.length > 1;
                    if (!tmpSigners.length) tmpSigners = library.cloneObj(this.defaultTemplateSigners);
                    this.templateSigners = library.cloneObj(tmpSigners);
                }
            }
            this.emailItems = tmpEmailControls;
            this.show = true;
        },
        updateSignersAfterDnD(items) {
            this.reloadSigners = true;
            let tmpItems = [];
            for (let i in items) {
                tmpItems.push({
                    "CONTACT_TYPE_ID": items[i].CONTACT_TYPE_ID,
                    "CONTACT_TYPE_REF_TABLE": items[i].CONTACT_TYPE_REF_TABLE,
                    "CUSTOM_ATTRIBUTE_ID": items[i].CUSTOM_ATTRIBUTE_ID,
                    "SIGNER_PSP": items[i].SIGNER_PSP,
                    "SIGNER_ORDER": items[i].SIGNER_ORDER,
                    "SIGNER_EMAIL": items[i].SIGNER_EMAIL,
                    "SIGNER_EST": items[i].SIGNER_EST
                });
            }
            this.tmpTemplateSigners = library.cloneObj(tmpItems);
            this.changeSigningOrder();
            this.$nextTick(() => {
                this.reloadSigners = false;
            });
        },
        changeSigningOrder() {
            this.reloadSigners = true;
            let tmpTemplateSigners = library.cloneObj(this.tmpTemplateSigners);
            for (let i in tmpTemplateSigners) {
                tmpTemplateSigners[i].SIGNER_ORDER = this.signingOrder ? parseInt(i, 10) + 1 : 1;
            }
            this.tmpTemplateSigners = tmpTemplateSigners;
            this.$nextTick(() => {
                this.reloadSigners = false;
            });
        }
    },
    components: {},
};

componentStore.initComponent(signerManagerCmp, signerManagerCfg, '@vms/signerManager.vue');
export { signerManagerCfg, signerManagerCmp };
export default signerManagerCmp;
</script>

<style scoped>

:deep(.current-volunteer-email) {
    padding: 9px;
    width: 390px;
    float: left;
}

:deep(.hidden) {
    display: none;
}

:deep(.e-listview .e-list-item.e-active) {
    background-color: #e0e0e0;
}
:deep(.signers-wrapper) {
    width: 500px;
    margin: 5px;
}
:deep(.signers-wrapper .control) {
    float: left;
}

:deep([name="SIGNERS_EDIT"]) {
    margin-left: 5px;
}
:deep(.signers-list-main-details .control) {
    margin-right: 5px;
}
:deep(.e-delete-icon::before) {
    content: "\e94a" !important;
}
:deep(.e-icons.e-drag-and-drop-indicator:before) {
    content: "\e22d" !important;
}
:deep(.drag-and-drop-signers) {
    cursor: pointer;
    padding: 9px;
}
:deep(.sub-list-signer-item-container) {
    margin: 5px;
    border: 1px solid lightgrey;
    padding: 10px;
}
:deep(.signer-color) {
    width: 5px;
    height: 70px;
    float: left;
    margin-right: 3px;
}
:deep(.signer-current-email .signer-color) {
    height: 32px;
}
:deep(.drag-and-drop-signers) {
    height: 70px;
}
:deep(.signer-current-email .drag-and-drop-signers) {
    height: 32px;
}
:deep(.drag-and-drop-signers .e-drag-and-drop-indicator) {
    position: relative;
    top: calc(50% - 6px);
}
:deep([name="SIGNERS"]) {
    max-height: 650px;
    overflow-y: auto;
    min-width: 500px;
}
:deep(.signers-actions) {
    margin: 5px;
}
:deep(.signers-actions .control) {
    width: 49%;
}
:deep(.signers-actions .control:first-child) {
    margin-right: 2%;
}
:deep(.e-delete-icon) {
    color: #d64113;
}
:deep(.set-signing-order-checkbox) {
    margin: 5px;
}
:deep(.e-ddt .e-filter-wrap) {
    display: none !important;
}

:deep(.signer-dropdown-color) {
    width: 20px;
    height: 20px;
    float: left;
    border-radius: 20px;
    margin: 8px 0 0 5px;
}
:deep(.signer-value-color) {
    width: 20px;
    height: 20px;
    float: left;
    border-radius: 20px;
    margin: 0 5px 0 0;
}
:deep(.signer-value) {
    width: 190px;
    overflow: hidden;
    text-overflow: ellipsis;
    height: 26px;
    line-height: 26px;
}

:deep(.message-for-save-btn) {
    text-align: right;
    margin-right: 10px;
    font-size: 14px;
}
:deep(.e-multi-select-wrapper .e-chips .e-chips-close::before) {
    content: "\e7e9" !important;
}
:deep(span[realid=SIGNER]) {
    width: 240px;
    padding-left: 10px;
    border-width: 1px !important;
    border-color: #bdbdbd !important;
    background-color: white !important;
}
</style>

<style>
.pdf-signer-dd .signer-dropdown-color {
    width: 20px;
    height: 20px;
    float: left;
    border-radius: 20px;
    margin: 8px 0 0 5px;
}
</style>
